import { FormControl, FormLabel, GridItem, Stack } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import gql from 'graphql-tag';
import { isNil } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { ConfigDisplayDefaultContentSource } from '~components/configurations/ConfigurationDetail/playback/ConfigDisplayDefaultContentSource';
import { ConfigDisplayLedStripColorSelect } from '~components/configurations/ConfigurationDetail/playback/ConfigDisplayLedStripColorSelect';
import { ConfigDisplayOrientationToggle } from '~components/configurations/ConfigurationDetail/playback/ConfigDisplayOrientationToggle';
import { isDefaultContentSourcesSupported as isDefaultContentSourcesSupportedByDisplay } from '~components/configurations/utils';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import { ConfigPlaybackSection_ConfigFragment } from './__generated__/PlaybackSection.graphql';
import { ConfigDisplayBrightnessSlider } from './ConfigDisplayBrightnessSlider';
import { ConfigDisplayBrightnessV2Slider } from './ConfigDisplayBrightnessV2Slider';
import { ConfigDisplayVolumeLimits } from './ConfigDisplayVolumeLimits';
import { ConfigDisplayVolumeSettings } from './ConfigDisplayVolumeSettings';

interface Props {
  config: ConfigPlaybackSection_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}

export function ConfigPlaybackSection({ config, configStatus }: Props) {
  const isDefaultContentSourcesSupported = isDefaultContentSourcesSupportedByDisplay(config);
  const { verifyUserPermissions } = useAuth();
  const [isBrightnessUpdateLoading, setIsBrightnessUpdateLoading] = useState(false);
  const [isLedStripUpdateLoading, setIsLedStripUpdateLoading] = useState(false);

  const hasConfigUpdatePermission =
    !checkStateToDisableTab(configStatus) &&
    verifyUserPermissions([Permission.ConfigurationUpdate]);
  const { t } = useTranslation();
  return (
    <>
      <GridItem colSpan={{ base: 8, md: 7 }}>
        <Stack direction="column" spacing="10">
          {isDefaultContentSourcesSupported && (
            <ConfigDisplayDefaultContentSource config={config} configStatus={configStatus} />
          )}
          <ConfigDisplayOrientationToggle config={config} configStatus={configStatus} />
        </Stack>
      </GridItem>

      {config.volume !== null && (
        <GridItem colSpan={{ base: 8, md: 7 }} mt={10} maxW={'md'}>
          <FormControl>
            <ConfigDisplayVolumeSettings config={config} configStatus={configStatus} />
          </FormControl>
        </GridItem>
      )}

      {isNil(config.brightnessV2) && !isNil(config.brightness) && (
        <GridItem colSpan={{ base: 8, md: 7 }} mt={10} maxWidth="md">
          <FormControl>
            <FormLabel>
              {t('brightness')}
              <FormLabelPendingIndicator isPending={isBrightnessUpdateLoading} />
            </FormLabel>
            <ConfigDisplayBrightnessSlider
              config={config}
              isDisabled={!hasConfigUpdatePermission}
              onBrightnessLoadingStateChange={setIsBrightnessUpdateLoading}
              configStatus={configStatus}
            />
          </FormControl>
        </GridItem>
      )}

      {isNil(config.brightness) && !isNil(config.brightnessV2) && (
        <GridItem colSpan={{ base: 8, md: 7 }} mt={10} maxWidth="md">
          <FormControl>
            <FormLabel>
              {t('brightness')}
              <FormLabelPendingIndicator isPending={isBrightnessUpdateLoading} />
            </FormLabel>
            <ConfigDisplayBrightnessV2Slider
              config={config}
              isDisabled={!hasConfigUpdatePermission}
              onBrightnessLoadingStateChange={setIsBrightnessUpdateLoading}
              configStatus={configStatus}
            />
          </FormControl>
        </GridItem>
      )}

      {config.ledStripColor !== null && (
        <GridItem colSpan={{ base: 8, sm: 4, lg: 8, xl: 3 }} mt={10}>
          <FormControl>
            <FormLabel>
              {t('ledStrip')}
              <FormLabelPendingIndicator isPending={isLedStripUpdateLoading} />
            </FormLabel>
            <ConfigDisplayLedStripColorSelect
              config={config}
              isDisabled={!hasConfigUpdatePermission}
              onLedStripLoadingStateChange={setIsLedStripUpdateLoading}
              configStatus={configStatus}
            />
          </FormControl>
        </GridItem>
      )}
      <ConfigDisplayVolumeLimits config={config} configStatus={configStatus} />
    </>
  );
}

ConfigPlaybackSection.graphql = {
  fragments: {
    ConfigPlaybackSection_config: gql`
      fragment ConfigPlaybackSection_config on ConfigurationModel {
        id
        ...ConfigDisplayDefaultContentSource_config
        ...ConfigDisplayOrientationToggle_config
        ...ConfigDisplayLedStripColorSelect_config
        ...ConfigDisplayBrightnessSlider_config
        ...ConfigDisplayBrightnessV2Slider_config
        ...ConfigDisplayVolumeSettings_config
        ...ConfigDisplayVolumeLimits_config
      }
    `,
  },
};
